import { useEffect, useMemo, useState } from "react";
import { CircleDollarSign, Eye, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MANAGE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, fmtBR, fmtBRL, fmtMonthBR, formatApiError } from "@/lib/api";

export const CHARGE_STATUS = {
  pendente: "Pendente",
  parcial: "Parcial",
  paga: "Paga",
  vencida: "Vencida",
  cancelada: "Cancelada",
};

export const ChargeStatusBadge = ({ status }) => (
  <Badge
    variant={status === "paga" ? "default" : status === "vencida" ? "destructive" : "secondary"}
    className={status === "paga" ? "bg-primary text-primary-foreground" : ""}
  >
    {CHARGE_STATUS[status] || status}
  </Badge>
);

const FIN_ACTIONS = {
  geracao: "Geração",
  pagamento: "Pagamento",
  estorno: "Estorno",
  desconto: "Desconto/Bolsa",
  cancelamento: "Cancelamento",
};

const currentMonth = () => new Date().toISOString().slice(0, 7);
const todayISO = () => new Date().toISOString().slice(0, 10);

function ChargeDetail({ charge, methods, canManage, onChanged }) {
  const remaining = Math.max(charge.amount - (charge.paid_amount || 0), 0);
  const isOpen = !["paga", "cancelada"].includes(charge.status);
  const [payForm, setPayForm] = useState({
    amount: remaining > 0 ? remaining.toFixed(2) : "",
    method: methods[0] || "",
    paid_at: todayISO(),
    notes: "",
  });
  const [discForm, setDiscForm] = useState({ discount_type: "percent", discount_value: "", reason: "" });
  const [cancelReason, setCancelReason] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reversing, setReversing] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const submitPayment = (e) => {
    e.preventDefault();
    run(
      "pay",
      () =>
        api.post(`/financial/charges/${charge.id}/payments`, {
          amount: Number(payForm.amount),
          method: payForm.method,
          paid_at: payForm.paid_at,
          notes: payForm.notes.trim() || undefined,
        }),
      "Pagamento registrado."
    );
  };

  const submitDiscount = (e) => {
    e.preventDefault();
    run(
      "discount",
      () =>
        api.post(`/financial/charges/${charge.id}/discount`, {
          discount_type: discForm.discount_type,
          discount_value: Number(discForm.discount_value),
          reason: discForm.reason,
        }),
      "Desconto/bolsa aplicado."
    );
  };

  const submitCancel = () =>
    run(
      "cancel",
      async () => {
        await api.post(`/financial/charges/${charge.id}/cancel`, { reason: cancelReason });
        setConfirmCancel(false);
      },
      "Cobrança cancelada."
    );

  const submitReverse = () =>
    run(
      "reverse",
      async () => {
        await api.delete(`/financial/payments/${reversing.id}`);
        setReversing(null);
      },
      "Pagamento estornado."
    );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Competência</p><p className="font-medium">{fmtMonthBR(charge.reference_month)}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Vencimento</p><p className="font-medium">{fmtBR(charge.due_date)}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><ChargeStatusBadge status={charge.status} /></div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Valor base</p><p className="font-medium">{fmtBRL(charge.amount_base)}</p></div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Desconto</p>
          <p className="font-medium" data-testid="charge-discount-summary">
            {charge.discount_type
              ? `${charge.discount_type === "percent" ? `${charge.discount_value}%` : fmtBRL(charge.discount_value)} · ${charge.discount_reason}`
              : "—"}
          </p>
        </div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Valor final</p><p className="font-medium" data-testid="charge-amount">{fmtBRL(charge.amount)}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Pago</p><p className="font-medium" data-testid="charge-paid">{fmtBRL(charge.paid_amount)}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Saldo</p><p className="font-medium" data-testid="charge-remaining">{fmtBRL(remaining)}</p></div>
        {charge.status === "cancelada" && (
          <div className="col-span-2 sm:col-span-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Motivo do cancelamento</p>
            <p className="font-medium" data-testid="charge-canceled-reason">{charge.canceled_reason}</p>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-base font-semibold">Pagamentos</h3>
        <div className="mt-2 space-y-2" data-testid="charge-payments-list">
          {(charge.payments || []).length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="charge-payments-empty">Nenhum pagamento registrado.</p>
          )}
          {(charge.payments || []).map((p) => (
            <div key={p.id} className={`flex flex-wrap items-center gap-2 border border-border p-3 ${p.reversed ? "opacity-60" : ""}`} data-testid={`charge-payment-${p.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {fmtBRL(p.amount)} · {p.method}
                  {p.reversed && <Badge variant="secondary" className="ml-2">Estornado</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fmtBR(p.paid_at)} · registrado por {p.registered_by_name}{p.notes ? ` · ${p.notes}` : ""}
                </p>
              </div>
              {canManage && !p.reversed && (
                <Button variant="ghost" size="icon" title="Estornar pagamento" onClick={() => setReversing(p)} data-testid={`payment-reverse-${p.id}`}>
                  <RotateCcw size={16} className="text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {isOpen && (
          <form onSubmit={submitPayment} className="mt-3 space-y-3 border border-border bg-zinc-50 p-3" data-testid="charge-payment-form">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registrar pagamento</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">Valor (R$)</Label>
                <Input type="number" min="0.01" step="0.01" max={remaining.toFixed(2)} required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="charge-pay-amount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Forma</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="charge-pay-method-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {methods.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data</Label>
                <Input type="date" required value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="charge-pay-date-input" />
              </div>
            </div>
            <Input placeholder="Observação (opcional)" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="charge-pay-notes-input" />
            <Button type="submit" size="sm" disabled={busy === "pay" || !payForm.method} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="charge-pay-submit">
              {busy === "pay" ? "Registrando..." : `Registrar pagamento (saldo ${fmtBRL(remaining)})`}
            </Button>
          </form>
        )}
      </div>

      {canManage && isOpen && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <form onSubmit={submitDiscount} className="space-y-3 border border-border bg-zinc-50 p-3" data-testid="charge-discount-form">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Desconto / bolsa</p>
            <div className="grid grid-cols-2 gap-2">
              <Select value={discForm.discount_type} onValueChange={(v) => setDiscForm({ ...discForm, discount_type: v })}>
                <SelectTrigger data-testid="charge-discount-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentual (%)</SelectItem>
                  <SelectItem value="value">Valor (R$)</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" min="0.01" step="0.01" required placeholder="Valor" value={discForm.discount_value} onChange={(e) => setDiscForm({ ...discForm, discount_value: e.target.value })} data-testid="charge-discount-value-input" />
            </div>
            <Input required placeholder="Motivo (obrigatório)" value={discForm.reason} onChange={(e) => setDiscForm({ ...discForm, reason: e.target.value })} data-testid="charge-discount-reason-input" />
            <Button type="submit" size="sm" variant="outline" disabled={busy === "discount"} data-testid="charge-discount-submit">
              {busy === "discount" ? "Aplicando..." : "Aplicar desconto"}
            </Button>
          </form>

          <div className="space-y-3 border border-destructive/40 bg-destructive/5 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-destructive">Cancelar cobrança</p>
            <Input placeholder="Motivo do cancelamento (obrigatório)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="charge-cancel-reason-input" />
            <Button type="button" size="sm" variant="destructive" disabled={cancelReason.trim().length < 3} onClick={() => setConfirmCancel(true)} data-testid="charge-cancel-button">
              Cancelar cobrança
            </Button>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-heading text-base font-semibold">Histórico</h3>
        <div className="mt-2 space-y-2" data-testid="charge-history-list">
          {(charge.history || []).map((h) => (
            <div key={h.id} className="border border-border p-2 text-xs" data-testid={`charge-history-${h.id}`}>
              <span className="font-semibold">{FIN_ACTIONS[h.action] || h.action}</span>
              <span className="text-muted-foreground"> · {h.detail} · {h.user_name} · {new Date(h.created_at).toLocaleString("pt-BR")}</span>
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancelar cobrança"
        description={`Cancelar a cobrança de ${fmtMonthBR(charge.reference_month)} no valor de ${fmtBRL(charge.amount)}? Motivo: ${cancelReason}`}
        onConfirm={submitCancel}
        testId="charge-cancel"
      />
      <ConfirmDialog
        open={!!reversing}
        onOpenChange={() => setReversing(null)}
        title="Estornar pagamento"
        description={reversing ? `Estornar ${fmtBRL(reversing.amount)} (${reversing.method}) de ${fmtBR(reversing.paid_at)}?` : ""}
        onConfirm={submitReverse}
        testId="payment-reverse"
      />
    </div>
  );
}

export default function Financial() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user.role);
  const [charges, setCharges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [methods, setMethods] = useState([]);
  const [filters, setFilters] = useState({ month: currentMonth(), status: "todos", course_id: "todos", search: "" });
  const [genMonth, setGenMonth] = useState(currentMonth());
  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const params = {};
    if (filters.month) params.reference_month = filters.month;
    if (filters.status !== "todos") params.status = filters.status;
    if (filters.course_id !== "todos") params.course_id = filters.course_id;
    if (filters.search.trim()) params.search = filters.search.trim();
    const [c, co, s] = await Promise.all([
      api.get("/financial/charges", { params }),
      api.get("/courses"),
      api.get("/settings"),
    ]);
    setCharges(c.data);
    setCourses(co.data);
    setMethods(s.data.payment_methods || []);
    setLoading(false);
  };

  useEffect(() => {
    load().catch((e) => {
      toast.error(formatApiError(e));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month, filters.status, filters.course_id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/financial/charges/generate", { reference_month: genMonth });
      toast.success(
        `Competência ${data.reference_month}: ${data.created} criada(s), ${data.skipped_existing} já existente(s), ${data.ignored_ended} ignorada(s).`
      );
      load().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const openDetail = async (charge) => {
    try {
      const { data } = await api.get(`/financial/charges/${charge.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const refreshDetail = async () => {
    try {
      const { data } = await api.get(`/financial/charges/${detail.id}`);
      setDetail(data);
      load().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totals = useMemo(() => {
    const t = { previsto: 0, recebido: 0, aberto: 0, vencidasValor: 0, vencidasCount: 0 };
    charges.forEach((c) => {
      if (c.status === "cancelada") return;
      t.previsto += c.amount;
      t.recebido += c.paid_amount || 0;
      const openAmount = c.amount - (c.paid_amount || 0);
      if (["pendente", "parcial", "vencida"].includes(c.status)) t.aberto += openAmount;
      if (c.status === "vencida") {
        t.vencidasValor += openAmount;
        t.vencidasCount += 1;
      }
    });
    return t;
  }, [charges]);

  const courseName = (id) => courses.find((c) => c.id === id)?.name || "—";

  if (loading) return <p className="text-sm text-muted-foreground">Carregando...</p>;

  return (
    <div className="space-y-6" data-testid="financial-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Financeiro</h1>
          <p className="mt-1 text-sm text-muted-foreground">Mensalidades, pagamentos, descontos e cancelamentos.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Competência</Label>
            <Input type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} data-testid="financial-generate-month-input" />
          </div>
          <Button onClick={generate} disabled={generating || !genMonth} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="financial-generate-button">
            <CircleDollarSign size={16} className="mr-2" />
            {generating ? "Gerando..." : "Gerar cobranças"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="financial-totals">
        <div className="border border-border bg-white p-4" data-testid="financial-total-previsto">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Previsto</p>
          <p className="mt-2 font-heading text-2xl font-bold">{fmtBRL(totals.previsto)}</p>
        </div>
        <div className="border border-border bg-white p-4" data-testid="financial-total-recebido">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Recebido</p>
          <p className="mt-2 font-heading text-2xl font-bold">{fmtBRL(totals.recebido)}</p>
        </div>
        <div className="border border-border bg-white p-4" data-testid="financial-total-aberto">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Em aberto</p>
          <p className="mt-2 font-heading text-2xl font-bold">{fmtBRL(totals.aberto)}</p>
        </div>
        <div className="border border-border bg-white p-4" data-testid="financial-total-vencidas">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Vencidas</p>
          <p className="mt-2 font-heading text-2xl font-bold">{fmtBRL(totals.vencidasValor)}</p>
          <p className="text-xs text-muted-foreground">{totals.vencidasCount} cobrança(s)</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        <form onSubmit={(e) => { e.preventDefault(); load().catch((err) => toast.error(formatApiError(err))); }} className="flex flex-1 items-center gap-2">
          <Input
            placeholder="Pesquisar por aluno..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="financial-search-input"
          />
          <Button type="submit" variant="outline" size="icon" data-testid="financial-search-button">
            <Search size={16} />
          </Button>
        </form>
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
          <Input type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} data-testid="financial-filter-month" />
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="financial-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(CHARGE_STATUS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.course_id} onValueChange={(v) => setFilters({ ...filters, course_id: v })}>
            <SelectTrigger data-testid="financial-filter-course"><SelectValue placeholder="Curso" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os cursos</SelectItem>
              {courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Aluno</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Competência</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Curso</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Vencimento</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Valor</TableHead>
              <TableHead className="hidden text-right text-xs uppercase tracking-wider sm:table-cell">Pago</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {charges.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-6 text-sm text-muted-foreground" data-testid="charges-empty">
                  Nenhuma cobrança encontrada. Use "Gerar cobranças" para criar as mensalidades da competência.
                </TableCell>
              </TableRow>
            )}
            {charges.map((c) => (
              <TableRow key={c.id} className="transition-colors hover:bg-zinc-50" data-testid={`charge-row-${c.id}`}>
                <TableCell className="font-medium">{c.student_name}</TableCell>
                <TableCell>{fmtMonthBR(c.reference_month)}</TableCell>
                <TableCell className="hidden md:table-cell">{courseName(c.course_id)}</TableCell>
                <TableCell className="hidden lg:table-cell">{fmtBR(c.due_date)}</TableCell>
                <TableCell className="text-right">{fmtBRL(c.amount)}</TableCell>
                <TableCell className="hidden text-right sm:table-cell">{fmtBRL(c.paid_amount)}</TableCell>
                <TableCell><ChargeStatusBadge status={c.status} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(c)} data-testid={`charge-view-${c.id}`}>
                    <Eye size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="charge-detail-dialog">
          <DialogHeader>
            <DialogTitle>Cobrança — {detail?.student_name} · {fmtMonthBR(detail?.reference_month)}</DialogTitle>
          </DialogHeader>
          {detail && (
            <ChargeDetail
              key={`${detail.id}:${detail.updated_at}`}
              charge={detail}
              methods={methods}
              canManage={canManage}
              onChanged={refreshDetail}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
